import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createChangeRequest,
  fetchChangeRequestAttachments,
  fetchChangeRequestLookup,
  fetchChangeRequests,
  promoteRiskLine,
  removeChangeRequestAttachment,
  setChangeRequestActive,
  updateChangeRequest,
  updateChangeRequestApprovalStatus,
  uploadChangeRequestAttachment,
  type CreateChangeRequestInput,
  type UpdateChangeRequestInput,
} from '../api/change-request.api';

import { RISK_KEYS } from './useRisks';

export const CHANGE_REQUEST_KEYS = {
  list: (projectId: string) => ['change-requests', projectId] as const,
  // Prefixed by list(projectId), so every mutation's invalidation of the list key also refreshes
  // the picker — approving/rejecting a CR changes what it should offer.
  lookup: (projectId: string) => ['change-requests', projectId, 'lookup'] as const,
  attachments: (projectId: string, crId: string) => ['change-requests', projectId, crId, 'attachments'] as const,
};

export function useChangeRequests(projectId: string) {
  return useQuery({
    queryKey: CHANGE_REQUEST_KEYS.list(projectId),
    queryFn: () => fetchChangeRequests(projectId),
    enabled: Boolean(projectId),
  });
}

// Approved-only list for the Task "Change Request" picker — reach for this instead of
// useChangeRequests() whenever populating that dropdown's selectable options (same split as
// useAccountLookup() vs useAccounts()).
export function useChangeRequestLookup(projectId: string) {
  return useQuery({
    queryKey: CHANGE_REQUEST_KEYS.lookup(projectId),
    queryFn: () => fetchChangeRequestLookup(projectId),
    enabled: Boolean(projectId),
  });
}

export function useCreateChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChangeRequestInput) => createChangeRequest(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.list(projectId) }),
  });
}

export function useUpdateChangeRequest(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ crId, data }: { crId: string; data: UpdateChangeRequestInput }) => updateChangeRequest(projectId, crId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.list(projectId) }),
  });
}

export function useUpdateChangeRequestApprovalStatus(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ crId, data }: { crId: string; data: { approvalStatus: string; approvedById?: string; approverRole?: string } }) =>
      updateChangeRequestApprovalStatus(projectId, crId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.list(projectId) }),
  });
}

export function usePromoteRiskLine(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ crId, lineId, ownerId }: { crId: string; lineId: string; ownerId: string }) =>
      promoteRiskLine(projectId, crId, lineId, ownerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.list(projectId) });
      // Promoting also creates a new Risk row server-side — without this, the Risks list stays
      // stale (cached under a completely different query key) until a full page reload.
      void queryClient.invalidateQueries({ queryKey: RISK_KEYS.list(projectId) });
    },
  });
}

export function useSetChangeRequestActive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ crId, active }: { crId: string; active: boolean }) => setChangeRequestActive(projectId, crId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.list(projectId) }),
  });
}

export function useChangeRequestAttachments(projectId: string, crId: string) {
  return useQuery({
    queryKey: CHANGE_REQUEST_KEYS.attachments(projectId, crId),
    queryFn: () => fetchChangeRequestAttachments(projectId, crId),
    enabled: Boolean(crId),
  });
}

export function useUploadChangeRequestAttachment(projectId: string, crId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadChangeRequestAttachment(projectId, crId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.attachments(projectId, crId) }),
  });
}

export function useRemoveChangeRequestAttachment(projectId: string, crId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeChangeRequestAttachment(projectId, crId, attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CHANGE_REQUEST_KEYS.attachments(projectId, crId) }),
  });
}
